import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, Button, Divider, Grid, IconButton, Modal, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import LoadingDialog from "../../../../../shared/components/LoadingModalDialog/LoadingModalDialog";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../../../shared/guards/ProfileGuard";
import { PlanServices } from "../../../../../shared/services/api/plan/PlanServices";
import { IProposalList } from "../../../../../shared/services/api/proposal/ProposalService";
import { globalMaskReal } from "../../../../../shared/utils";
import { Plan } from "./PlanCard";
import PlanCardHorizontal from "./PlanCardHorizontal";

interface ModalPlanRenovateProps {
	isOpen: boolean;
	onClose: () => void;
	onSelectPlan: (plan: Plan) => void;
	row: IProposalList;
	updatedRentAmount: string;
	updateIPTU: string;
	updateCondominium: string;
	installmentsCount?: number;
}

/**
 * O hook `useFetchPlans` é responsável por buscar planos atualizados com base nos valores fornecidos de aluguel, IPTU e condomínio.
 * Quando o modal está aberto (`isOpen` é true) e a linha (`row`) contém um id válido e um valor de aluguel atualizado,
 * ele faz uma chamada assíncrona ao serviço `PlanServices.planUpdateAmountClient` para obter os planos.
 * 
 * A resposta é filtrada para incluir apenas planos com uma nova taxa (`newFee`) maior ou igual à taxa residencial (`residentialFee`),
 * dependendo da diferença em meses desde a última atualização (`adjustmentAt`).
 */
const resolveAmounts = (row: any, updatedRentAmount: number, updateIPTU: number, updateCondominium: number) => {
	const rentAmount = updatedRentAmount !== undefined && updatedRentAmount !== 0 ? updatedRentAmount : parseFloat(row.rentAmount || "0");
	const iptu = updateIPTU !== undefined && updateIPTU !== 0 ? updateIPTU : parseFloat(row.iptuAmount || "0");
	const condominium = updateCondominium !== undefined && updateCondominium !== 0 ? updateCondominium : parseFloat(row.condominiumAmount || "0");
	return { rentAmount, iptu, condominium };
};

const useFetchPlans = (isOpen: boolean, row: any, updatedRentAmount: number, updateIPTU: number, updateCondominium: number) => {
	const [plans, setPlans] = useState<Plan[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (isOpen && row?.id && updatedRentAmount !== undefined) {
			(async () => {
				setLoading(true);
				setError(null);
				try {
					const { rentAmount, iptu, condominium } = resolveAmounts(row, updatedRentAmount, updateIPTU, updateCondominium);
					const response = await PlanServices.planUpdateAmountClient(row.id, rentAmount.toString(), iptu.toString(), condominium.toString());

					if (response instanceof Error) {
						throw response;
					}

					if (Array.isArray(response)) {
						const diffInMonths = Math.floor((new Date().getTime() - new Date(row.adjustmentAt).getTime()) / (1000 * 60 * 60 * 24 * 30));
						const filteredPlans = response.filter((item) => {
							const newFee = parseFloat(item.newFee || "0");
							const residentialFee = parseFloat(row.residentialFee || "0");
							if (diffInMonths > 2) { // 60 dias é aproximadamente 2 meses
								return newFee >= residentialFee;
							} else {
								return newFee > residentialFee;
							}
						});

						if (filteredPlans.length > 0) {
							setPlans(filteredPlans.map((item) => mapPlanData(item, rentAmount, condominium, iptu)));
						} else {
							// Nenhum plano com taxa igual ou superior foi encontrado: mantém o plano atual disponível para renovação.
							const currentItem = response.find((item) => (item.plan?.id ?? item.id) === row.planId);
							setPlans([
								currentItem
									? mapPlanData(currentItem, rentAmount, condominium, iptu)
									: buildCurrentPlanFromRow(row, rentAmount, condominium, iptu),
							]);
						}
					} else {
						setError("Ocorreu um erro ao carregar os planos. Tente novamente.");
					}
				} catch (err) {
					setError("Erro ao carregar os planos. Verifique sua conexão ou tente novamente.");
				} finally {
					setLoading(false);
				}
			})();
		}
	}, [isOpen, row, updatedRentAmount, updateIPTU, updateCondominium]);

	return { plans, loading, error };
};

const mapPlanData = (item: any, rentAmount: number, condominium: number, iptu: number): Plan => {
	const newFee = parseFloat(item.newFee).toFixed(2) || item.newFee;
	const calculatedPrice = item.flagRateCharge === "1" ? (newFee * (rentAmount + condominium + iptu)) / 100 : (newFee * rentAmount) / 100;
	const planLmi = item.lmi ? parseInt(item.lmi, 10) : 0;
	const lmiValue =
		item.flagRateCharge === "1" ? parseFloat((planLmi * (rentAmount + condominium + iptu)).toFixed(2)) : parseFloat((planLmi * rentAmount).toFixed(2));

	return {
		id: item.plan?.id || item.id || "sem-id",
		name: item.plan?.description || "Plano Desconhecido",
		price: calculatedPrice,
		description: `Plano ${item.plan?.description || "Desconhecido"}`,
		lmiValue,
		lmi: planLmi,
		commission: item.plan?.bonusAmount ? parseFloat(item.plan.bonusAmount) : undefined,
		taxa: item.newFee ? parseFloat(item.newFee) : undefined,
		flagRateCharge: item.flagRateCharge,
	};
};

const buildCurrentPlanFromRow = (row: any, rentAmount: number, condominium: number, iptu: number): Plan => {
	const fee = parseFloat(row.residentialFee || row.fee || "0");
	const flagRateCharge = row.plan?.flagRateCharge;
	const calculatedPrice = flagRateCharge === "1" ? (fee * (rentAmount + condominium + iptu)) / 100 : (fee * rentAmount) / 100;
	const planLmi = row.plan?.lmi ? Number(row.plan.lmi) : row.lmi ? Number(row.lmi) : 0;
	const lmiValue =
		flagRateCharge === "1" ? parseFloat((planLmi * (rentAmount + condominium + iptu)).toFixed(2)) : parseFloat((planLmi * rentAmount).toFixed(2));

	return {
		id: row.planId || row.plan?.id || "atual",
		name: row.plan?.description || "Plano Atual",
		price: calculatedPrice,
		description: `Plano ${row.plan?.description || "Atual"}`,
		lmiValue,
		lmi: planLmi,
		commission: row.plan?.bonusAmount ? parseFloat(row.plan.bonusAmount) : undefined,
		taxa: fee,
		flagRateCharge,
	};
};

const ModalPlanRenovate: React.FC<ModalPlanRenovateProps> = ({
	isOpen,
	onClose,
	onSelectPlan,
	row,
	updatedRentAmount,
	updateIPTU,
	updateCondominium,
	installmentsCount,
}) => {
	const { plans, loading, error } = useFetchPlans(isOpen, row, parseFloat(updatedRentAmount), parseFloat(updateIPTU), parseFloat(updateCondominium));
	const [adjustingPlan, setAdjustingPlan] = useState<Plan | null>(null);
	const [reajustePercent, setReajustePercent] = useState<string>("0");
	// true somente quando o plano atual foi exibido por não haver nenhum plano com taxa superior disponível
	const [isNoSuperiorPlanFound, setIsNoSuperiorPlanFound] = useState(false);
	const canKeepCurrentPlan = checkByProfileGuard(PermissionRolesEnum.WarrantiesOptions, PermissionActionsEnum.RenewKeepCurrentPlan);

	useEffect(() => {
		if (!isOpen) {
			setAdjustingPlan(null);
			setReajustePercent("0");
			setIsNoSuperiorPlanFound(false);
		}
	}, [isOpen]);

	const handleSelectPlan = (plan: Plan) => {
		if (String(plan.id) === String(row.planId)) {
			// Só é "nenhum plano encontrado" quando o atual é a única opção retornada
			setIsNoSuperiorPlanFound(plans.length === 1);
			setAdjustingPlan(plan);
			setReajustePercent("0");
			return;
		}
		onSelectPlan(plan);
		onClose();
	};

	const handleBackToPlans = () => {
		setAdjustingPlan(null);
		setReajustePercent("0");
		setIsNoSuperiorPlanFound(false);
	};

	const handleKeepCurrentPlan = () => {
		const { rentAmount, iptu, condominium } = resolveAmounts(row, parseFloat(updatedRentAmount), parseFloat(updateIPTU), parseFloat(updateCondominium));
		setAdjustingPlan(buildCurrentPlanFromRow(row, rentAmount, condominium, iptu));
		setIsNoSuperiorPlanFound(false);
		setReajustePercent("0");
	};

	const reajusteNumber = Math.min(20, Math.max(0, parseFloat(reajustePercent.replace(",", ".")) || 0));
	const installments = installmentsCount || Number(row.rentMonthsCount) || 12;
	const newPrice = adjustingPlan ? adjustingPlan.price * (1 + reajusteNumber / 100) : 0;

	const handleConfirmAdjustment = () => {
		if (!adjustingPlan) return;
		onSelectPlan({ ...adjustingPlan, price: newPrice, adjustmentAmount: reajusteNumber });
		onClose();
	};

	const showKeepCurrentPlanButton =
		canKeepCurrentPlan && !!row.planId && plans.length > 0 && !plans.some((plan) => String(plan.id) === String(row.planId));

	return (
		<Modal open={isOpen} onClose={onClose}>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					width: "95%",
					maxWidth: adjustingPlan ? 820 : 1000,
					maxHeight: "90vh",
					bgcolor: "background.paper",
					boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
					p: 4,
					borderRadius: 2,
					background: "#fff",
				}}
			>
				<Typography variant={"h4"} sx={{ fontWeight: 300, textAlign: "center", mb: 1 }} color={"primary"}>
					{adjustingPlan ? "Confirmação de Renovação" : "Escolha seu Plano para Renovação"}
				</Typography>
				<Box sx={{ position: "absolute", top: 8, right: 8 }}>
					<IconButton onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</Box>

				{adjustingPlan ? (
					<Box sx={{ mt: 4, maxWidth: 820, mx: "auto" }}>
						<Grid container spacing={3} alignItems="flex-start">
							{/* Coluna esquerda: aviso, plano em destaque e campo de reajuste */}
							<Grid item xs={12} md={6}>
								{isNoSuperiorPlanFound && (
									<Alert severity="info" sx={{ mb: 2 }}>
										Nenhum plano com taxa superior à atual foi encontrado. Informe o percentual de reajuste a ser aplicado sobre o valor
										atual da parcela para renovar com o mesmo plano.
									</Alert>
								)}

								<Box
									sx={{
										background: "linear-gradient(135deg, #64B5F6 0%, #9C27B0 100%)",
										color: "#fff",
										borderRadius: 2,
										p: 2,
										mb: 3,
									}}
								>
									<Typography variant="caption" sx={{ opacity: 0.85 }}>
										Você está renovando com o plano
									</Typography>
									<Typography variant="h6" sx={{ fontWeight: 700 }}>
										{adjustingPlan.name}
									</Typography>
									{adjustingPlan.taxa !== undefined && (
										<Typography variant="body2" sx={{ opacity: 0.9 }}>
											Taxa Garanti atual: {adjustingPlan.taxa.toFixed(2)}%
										</Typography>
									)}
								</Box>

								<TextField
									label="Percentual de reajuste (%)"
									type="number"
									value={reajustePercent}
									onChange={(e) => {
										const raw = e.target.value;
										if (raw === "") {
											setReajustePercent(raw);
											return;
										}
										const parsed = parseFloat(raw.replace(",", "."));
										if (isNaN(parsed)) {
											setReajustePercent(raw);
											return;
										}
										setReajustePercent(Math.min(20, Math.max(0, parsed)).toString());
									}}
									fullWidth
									inputProps={{ step: "0.01", min: 0, max: 20 }}
									helperText="Permitido de 0% a 20%"
								/>
							</Grid>

							{/* Coluna direita: resumo */}
							<Grid item xs={12} md={6}>
								<Box
									sx={{
										textAlign: "left",
										background: "#f7f7f9",
										border: "1px solid #e0e0e0",
										borderRadius: 2,
										p: 2,
									}}
								>
									<Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
										Resumo da renovação
									</Typography>
									<Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
										<Typography sx={{ color: "#666" }}>Plano</Typography>
										<Typography>{adjustingPlan.name}</Typography>
									</Box>
									{adjustingPlan.taxa !== undefined && (
										<Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
											<Typography sx={{ color: "#666" }}>Taxa Garanti atual</Typography>
											<Typography>{adjustingPlan.taxa.toFixed(2)}%</Typography>
										</Box>
									)}
									<Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
										<Typography sx={{ color: "#666" }}>Valor atual da parcela</Typography>
										<Typography>R$ {globalMaskReal(adjustingPlan.price.toFixed(2))}</Typography>
									</Box>
									<Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
										<Typography sx={{ color: "#666" }}>Reajuste aplicado</Typography>
										<Typography>{reajusteNumber.toFixed(2)}%</Typography>
									</Box>
									<Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5, fontWeight: 600 }}>
										<Typography sx={{ color: "#666" }}>Novo valor da parcela</Typography>
										<Typography sx={{ fontWeight: 600 }}>R$ {globalMaskReal(newPrice.toFixed(2))}</Typography>
									</Box>
									<Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
										<Typography sx={{ color: "#666" }}>Quantidade de parcelas</Typography>
										<Typography>{installments}</Typography>
									</Box>
									<Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
										<Typography sx={{ color: "#666" }}>Total da vigência</Typography>
										<Typography>R$ {globalMaskReal((newPrice * installments).toFixed(2))}</Typography>
									</Box>
								</Box>
							</Grid>
						</Grid>

						<Divider sx={{ mt: 3 }} />

						<Box sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 3 }}>
							<Button variant="outlined" onClick={handleBackToPlans}>
								Voltar
							</Button>
							<Button variant="contained" sx={{ backgroundColor: "#8257e6" }} onClick={handleConfirmAdjustment}>
								Confirmar e Renovar
							</Button>
						</Box>
					</Box>
				) : loading ? (
					<Box sx={{ textAlign: "center", mt: 4 }}>
						{loading && <LoadingDialog isLoading={loading} loadingText={`Carregando planos disponíveis...`} />}
					</Box>
				) : error ? (
					<Typography
						variant="body1"
						color="error"
						sx={{
							mt: 4,
							textAlign: "center",
							fontFamily: "'Inter', sans-serif",
						}}
					>
						{error}
					</Typography>
				) : plans.length === 0 ? (
					<Typography
						variant="body1"
						sx={{
							mt: 8,
							textAlign: "center",
							color: "#666",
							fontFamily: "'Inter', sans-serif",
						}}
					>
						Nenhum plano disponível para renovação no momento, contate o setor comercial.
					</Typography>
				) : (
					<Box
						sx={{
							maxHeight: "70vh",
							overflowY: "auto",
							mt: 3,
							px: 1,
						}}
					>
						{showKeepCurrentPlanButton && (
							<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
								<Button variant="outlined" onClick={handleKeepCurrentPlan}>
									Manter Plano Atual
								</Button>
							</Box>
						)}
						{plans.map((plan) => (
							<PlanCardHorizontal key={plan.id} plan={plan} currentPlanId={row.planId || ""} onSelect={handleSelectPlan} />
						))}
					</Box>
				)}
			</Box>
		</Modal>
	);
};

export default ModalPlanRenovate;
